'use client';
import { isNil } from 'lodash-es';
import type React from 'react';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { GoChevronRight } from 'react-icons/go';
import { MdOutlineTimer } from 'react-icons/md';
import { formatDay } from '../../../common/formatter';
import useAvailableAppointmentsModal from '../../../hooks/useAvailableAppointmentsModal';
import useAvailableBusinessesModal from '../../../hooks/useAvailableBusinessesModal';
import useAvailableServicesModal from '../../../hooks/useAvailableServicesModal';
import useContactConfirmationModal from '../../../hooks/useContactConfirmationModal';
import useLoginModal from '../../../hooks/useLoginModal';
import useTimezone from '../../../hooks/useTimezone';
import useUser from '../../../hooks/useUser';
import type { CartSelectionType } from '../../../redux/ducks/CartDucks.types';
import Button from '../../button/Button';

interface PartnerSeoPageProps {
  currentCart: CartSelectionType | undefined;
}

const PartnerSeoCart: React.FC<PartnerSeoPageProps> = ({ currentCart }) => {
  const { t, i18n } = useTranslation();
  const { currentTimezone } = useTimezone();
  const availableBusinessesModal = useAvailableBusinessesModal();
  const availableAppointmentsModal = useAvailableAppointmentsModal();
  const availableServicesModal = useAvailableServicesModal();
  const confirmContactModal = useContactConfirmationModal();
  const loginModal = useLoginModal();
  const { isLoggedIn } = useUser();

  const [onClient, setOnClient] = useState<boolean>(false);

  useEffect(() => {
    setOnClient(true);
  }, []);

  const onContact = useCallback(() => {
    if (isLoggedIn) {
      confirmContactModal.onOpen();
    } else {
      loginModal.onOpen();
    }
  }, [isLoggedIn, loginModal.onOpen, confirmContactModal.onOpen]);

  const formattedDay = useMemo<string>(() => {
    if (isNil(currentCart)) {
      return '';
    }

    return formatDay({
      timestamp: currentCart.from,
      timezone: currentTimezone,
      language: i18n.language,
      t,
      shortDay: false,
      shortMonth: true,
      noYear: true,
    });
  }, [currentCart, t, i18n, currentTimezone]);

  const formattedTimeWindow = useMemo<string>(() => {
    if (isNil(currentCart)) {
      return '';
    }

    return `${Intl.DateTimeFormat(i18n.language, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
      timeZone: currentTimezone,
    }).format(new Date(currentCart.from))}
           · 
          ${Intl.DateTimeFormat(i18n.language, {
            hour: 'numeric',
            minute: 'numeric',
            hour12: false,
            timeZone: currentTimezone,
          }).format(new Date(currentCart.to))}`;
  }, [currentCart, i18n, currentTimezone]);

  const businessesSection = useCallback(
    (cart: CartSelectionType) => (
      <button
        id="partner-page-cart-more-businesses"
        data-test-id="partner-page-cart-more-businesses"
        data-umami-event="partner-page-cart-more-businesses-selected"
        data-umami-event-source="cart"
        className="flex h-full min-h-[64px] min-w-full cursor-pointer flex-col items-center justify-center border-b border-solid border-slate-300 py-3"
        type="button"
        onClick={() => availableBusinessesModal.onOpen()}
      >
        <div className="flex h-full min-w-full flex-row">
          <div className="mr-2 flex min-h-full grow flex-col items-start justify-center">
            <span className="line-clamp-1 overflow-hidden text-ellipsis break-all font-bold capitalize">
              {cart?.business?.name
                ? cart?.business?.name
                : cart?.partner.fullName}
            </span>
            <span className="line-clamp-1 overflow-hidden text-ellipsis break-all text-sm capitalize">
              {cart?.business?.fullAddress}
            </span>
          </div>
          <div className="mr-1 flex min-h-full flex-col items-center justify-center">
            <GoChevronRight size={24} />
          </div>
        </div>
      </button>
    ),
    [availableBusinessesModal.onOpen],
  );

  const serviceSection = useCallback(
    (cart: CartSelectionType) => (
      <button
        id="partner-page-more-services"
        key="partner-page-more-services"
        aria-label={t('static.generic.show_more_services')}
        data-test-id="partner-page-more-services"
        data-umami-event="partner-page-more-services-selected"
        data-umami-event-source="cart"
        className="flex h-full min-h-[64px] min-w-full cursor-pointer flex-col items-center justify-center border-b border-solid border-slate-300 py-3"
        type="button"
        onClick={() => availableServicesModal.onOpen()}
      >
        <div className="flex h-full min-w-full flex-row">
          <div className="mr-2 flex min-h-full grow flex-col items-start justify-center">
            <span className="line-clamp-1 overflow-hidden text-ellipsis break-all font-bold capitalize">
              {cart?.service?.name}
            </span>
            <span className="flex flex-row">
              <span className="mr-0.5 flex h-full flex-col items-center justify-center">
                <MdOutlineTimer size={16} />
              </span>
              <span className="line-clamp-1 flex h-full grow flex-col items-center justify-center overflow-hidden text-ellipsis break-all text-sm">
                {t('static.profile_page.service_duration_label', {
                  duration: Math.floor((cart?.service?.duration || 0) / 60),
                })}
              </span>
            </span>
          </div>
          <div className="mr-1 flex min-h-full flex-col items-center justify-center">
            <GoChevronRight size={24} />
          </div>
        </div>
      </button>
    ),
    [t, availableServicesModal.onOpen],
  );

  const cartBody = useCallback(
    (cart: CartSelectionType) => (
      <div className="flex flex-col items-start justify-start">
        <div className="flex w-full flex-col items-start justify-start gap-0 text-left">
          <span className="line-clamp-1 min-w-16 overflow-hidden text-ellipsis break-all text-left text-2xl font-bold">
            {new Intl.NumberFormat(i18n.language, {
              style: 'currency',
              currency: cart.service.currency,
            }).format(cart.service.price)}
          </span>
        </div>
        <div className="mt-4 flex w-full flex-col rounded-md">
          {serviceSection(cart)}
          {businessesSection(cart)}
          {onClient && (
            <Suspense fallback={<div className="hidden">&nbsp;</div>}>
              <button
                id="partner-page-more-hours"
                key="partner-page-more-hours"
                aria-label={t('static.generic.show_more_options')}
                data-test-id="partner-page-more-hours"
                data-umami-event="partner-page-more-hours-selected"
                data-umami-event-source="cart"
                className="flex h-full min-h-[64px] min-w-full cursor-pointer flex-col items-center justify-center py-3"
                type="button"
                onClick={() => availableAppointmentsModal.onOpen()}
              >
                <div className="flex h-full min-w-full flex-row">
                  <div className="mr-2 flex min-h-full grow flex-col items-start justify-center">
                    <span className="line-clamp-1 overflow-hidden text-ellipsis break-all font-bold capitalize">
                      {formattedDay}
                    </span>
                    <span className="line-clamp-1 overflow-hidden text-ellipsis break-all text-sm">
                      {formattedTimeWindow}
                    </span>
                  </div>
                  <div className="mr-1 flex min-h-full flex-col items-center justify-center">
                    <GoChevronRight size={24} />
                  </div>
                </div>
              </button>
            </Suspense>
          )}
        </div>
        <div className="mt-8 flex w-full flex-col items-center justify-center gap-2">
          <Button
            disabled={false}
            loading={false}
            label={t('static.cart.appointment_action_button_label')}
            onClick={onContact}
            data-test-id="cart-contact-button"
            id="cart-contact-button"
          />
          <span className="min-w-16 text-center text-sm font-semibold italic text-slate-500">
            {t('static.cart.no_payment_required')}
          </span>
        </div>
      </div>
    ),
    [
      formattedDay,
      formattedTimeWindow,
      i18n.language,
      serviceSection,
      businessesSection,
      t,
      availableAppointmentsModal.onOpen,
      onContact,
      onClient,
    ],
  );

  const unavailableCartBody = useCallback(
    () => (
      <div className="flex size-full h-56 flex-col items-start justify-start">
        <div className="mt-4 flex w-full flex-col rounded-md">
          <span className="mb-4 min-w-full text-sm">
            <Trans i18nKey="static.cart.no_slots_available_description">
              <span className="font-bold">&nbsp;</span>
            </Trans>
          </span>
          <button
            id="partner-page-more-hours"
            key="partner-page-more-hours"
            aria-label={t('static.generic.show_more_options')}
            data-test-id="partner-page-more-hours"
            data-umami-event="partner-page-more-hours-selected"
            data-umami-event-source="cart"
            className="group flex h-full min-h-[64px] min-w-full cursor-pointer flex-col items-center justify-center py-3 hover:underline"
            type="button"
            onClick={() => availableAppointmentsModal.onOpen()}
          >
            <div className="flex h-full min-w-full flex-row">
              <div className="mr-2 flex min-h-full grow flex-col items-start justify-center">
                <span className="line-clamp-1 overflow-hidden text-ellipsis break-all font-bold">
                  {t('static.cart.change_time_main')}
                </span>
              </div>
              <div className="mr-1 flex min-h-full flex-col items-center justify-center group-hover:pr-2">
                <GoChevronRight size={24} />
              </div>
            </div>
          </button>
          <button
            id="partner-page-more-places"
            key="partner-page-more-places"
            aria-label={t('static.generic.show_more_businesses')}
            data-test-id="partner-page-more-places"
            data-umami-event="partner-page-more-places-selected"
            data-umami-event-source="cart"
            className="group flex h-full min-h-[64px] min-w-full cursor-pointer flex-col items-center justify-center py-3 hover:underline"
            type="button"
            onClick={() => availableBusinessesModal.onOpen()}
          >
            <div className="flex h-full min-w-full flex-row">
              <div className="mr-2 flex min-h-full grow flex-col items-start justify-center">
                <span className="line-clamp-1 overflow-hidden text-ellipsis break-all font-bold">
                  {t('static.cart.change_business_main')}
                </span>
              </div>
              <div className="mr-1 flex min-h-full flex-col items-center justify-center group-hover:pr-2">
                <GoChevronRight size={24} />
              </div>
            </div>
          </button>
        </div>
      </div>
    ),
    [t, availableAppointmentsModal.onOpen, availableBusinessesModal.onOpen],
  );

  return (
    <div className="flex min-h-64 min-w-80 max-w-80 rounded-2xl border border-solid border-slate-300 p-3 shadow sticky top-24 flex-col z-20">
      {isNil(currentCart) ? unavailableCartBody() : cartBody(currentCart)}
    </div>
  );
};

export default PartnerSeoCart;
